import NavItem from "@/features/Navigation/components/NavItem";
import SearchBar from "@/features/Navigation/components/SearchBar";
import useNavigation from "@/features/Navigation/hooks/useNavigation";
import useSearch from "@/features/Navigation/hooks/useSearch";
import { NavSectionConfig } from "@/features/Navigation/types/Navigation.types";
import { Accordion } from "@/ui/organisms/Accordion/Accordion";
import { useAccordionList } from "@/ui/organisms/Accordion/useAccordionList";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useFlags } from "@/utils/flagsmith/useFlags";

export function MemoNavigator() {
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");

    const sections: NavSectionConfig[] = useMemo(
        () => [
            {
                id: "investor-memo",
                title: "Executive Summary",
                defaultOpen: true,
                items: [
                    {
                        id: "company-overview",
                        label: "Company Overview",
                        anchor: "company-overview",
                    },
                    {
                        id: "transaction-summary",
                        label: "Transaction Summary",
                        anchor: "transaction-summary",
                    },
                    {
                        id: "deal-structure",
                        label: "Deal Structure",
                        anchor: "deal-structure",
                    },
                    {
                        id: "financial-highlights",
                        label: "Financial Highlights",
                        anchor: "financial-highlights",
                    },
                    {
                        id: "investment-rationale",
                        label: "Investment Rationale",
                        anchor: "investment-rationale",
                    },
                    {
                        id: "due-diligence",
                        label: "Due Diligence",
                        anchor: "due-diligence",
                    },
                    ...(showComingSoon
                        ? [
                              {
                                  id: "key-risks",
                                  label: "Key Risks",
                                  anchor: "key-risks",
                              },
                              {
                                  id: "funding-and-valuation",
                                  label: "Funding & Valuation",
                                  anchor: "funding-and-valuation",
                              },
                          ]
                        : []),
                    {
                        id: "industry-overview",
                        label: "Industry & Value Chain Analysis",
                        anchor: "industry-overview",
                    },
                ],
            },
        ],
        [showComingSoon],
    );

    const allItems = useMemo(
        () =>
            sections.flatMap((section) =>
                (section.items || []).map((item) => ({
                    ...item,
                    searchableText: item.label,
                    sectionId: section.id,
                    sectionTitle: section.title,
                })),
            ),
        [sections],
    );
    const [activeItemId, setActiveItemId] = useState<string>();
    const { handleNavigation } = useNavigation({
        onNavigate: (item) => setActiveItemId(item.id),
        scrollBehavior: "smooth",
    });

    const { searchTerm, setSearchTerm, filteredItems } = useSearch({
        items: allItems,
        searchFields: ["searchableText", "label"],
    });

    const filteredSections = useMemo(() => {
        if (!searchTerm.trim() || !sections) return sections;

        const sectionsWithItems = sections
            .map((section) => ({
                ...section,
                items: filteredItems.filter(
                    (item) => item.sectionId === section.id,
                ),
            }))
            .filter((section) => section.items && section.items.length > 0);

        return sectionsWithItems;
    }, [sections, filteredItems, searchTerm]);

    const initialExpanded = useMemo(
        () =>
            sections
                .filter((section) => section.defaultOpen)
                .map((section) => section.id),
        [sections],
    );

    const { expanded, setExpanded, onClick } = useAccordionList({
        allowMultiple: true,
        initialExpanded,
    });

    useEffect(() => {
        if (searchTerm.trim()) {
            setExpanded(filteredSections.map((section) => section.id));
        }
    }, [searchTerm, filteredSections, setExpanded]);

    return (
        <div className="flex flex-col gap-3 sm:px-3">
            <SearchBar value={searchTerm} onChange={setSearchTerm} />
            <div
                className={clsx(
                    "flex min-h-0 flex-1 flex-col gap-2",
                    "overflow-y-auto overflow-x-hidden",
                )}
            >
                {searchTerm.trim() && filteredSections?.length === 0 ? (
                    <div
                        className={clsx(
                            "px-2 py-4",
                            "subdued text-center",
                            "body-s",
                        )}
                    >
                        No results found for &quot;{searchTerm}&quot;
                    </div>
                ) : (
                    filteredSections?.map((section) => (
                        <Accordion
                            key={section.id}
                            i={section.title}
                            isOpen={expanded.includes(section.id)}
                            onClick={() => onClick(section.id)}
                        >
                            <div className="body-s flex flex-col gap-1">
                                {section.items?.map((item) => (
                                    <NavItem
                                        key={item.id}
                                        label={item.label}
                                        active={activeItemId === item.id}
                                        item={item}
                                        onNavigate={handleNavigation}
                                    />
                                ))}
                            </div>
                        </Accordion>
                    ))
                )}
            </div>
        </div>
    );
}
